import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import { useSearchBox } from 'react-instantsearch';
import '../../../../algolia-search/scss/main.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CalendarToolbar from '@atoms/CalendarToolbar';
import EventCardHover from '@molecules/EventCardHover';
import InfoIcon from '@icons/InfoIcon';
import CloseIcon from '@icons/CloseIcon';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const DISMISS_DELAY = 100;

const CalendarEvent = ({ event, setActiveEvent, cancelDismiss, scheduleDismiss }) => {
    const spanRef = useRef(null);

    const handleMouseEnter = useCallback(() => {
        cancelDismiss();
        if (spanRef.current) {
            const rect = spanRef.current.getBoundingClientRect();
            setActiveEvent({ event, anchorRect: rect, anchorEl: spanRef.current });
        }
    }, [event, setActiveEvent, cancelDismiss]);

    const handleClick = useCallback(() => {
        const url = event.resource?.url || event.resource?.permalink;
        if (url) window.location.href = url;
    }, [event]);

    return (
        <span
            ref={spanRef}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={scheduleDismiss}
            style={{ cursor: 'pointer' }}
        >
            {event.title}
        </span>
    );
};

const CalendarToolbarWrapper = ({ visibleDate, setVisibleDate, ...props }) => (
    <CalendarToolbar {...props} date={visibleDate} onDateChange={setVisibleDate} />
);
CalendarToolbarWrapper.displayName = 'CalendarToolbarWrapper';

const EventCalendar = ({ hits }) => {
    const [visibleDate, setVisibleDate] = useState(new Date());
    const [activeEvent, setActiveEvent] = useState(null);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const dismissTimer = useRef(null);
    const { query } = useSearchBox();

    useEffect(() => {
        if (query) setBannerDismissed(false);
    }, [query]);

    useEffect(() => {
        return () => {
            clearTimeout(dismissTimer.current);
        };
    }, []);

    const cancelDismiss = useCallback(() => clearTimeout(dismissTimer.current), []);
    const scheduleDismiss = useCallback(() => {
        dismissTimer.current = setTimeout(() => setActiveEvent(null), DISMISS_DELAY);
    }, []);

    const events = useMemo(
        () =>
            hits
                .filter(hit => hit.meeting_start_time)
                .map(hit => ({
                    title: hit.title,
                    start: new Date(hit.meeting_start_time),
                    end: new Date(hit.meeting_end_time || hit.meeting_start_time),
                    resource: hit,
                })),
        [hits]
    );

    const handleSetActiveEvent = useCallback(payload => {
        setActiveEvent(payload ?? null);
    }, []);

    return (
        <div style={{ height: '900px', position: 'relative', display: 'flex', flexDirection: 'column', paddingBottom: '60px' }}>
            {query && !bannerDismissed && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#FDEA9B',
                        padding: '8px 12px',
                        marginBottom: 16,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <InfoIcon />
                        </div>
                        <div
                            style={{
                                fontFamily: 'Red Hat Display',
                                color: '#343434',
                                fontSize: 14,
                                lineHeight: '21px',
                                textAlign: 'left',
                                fontWeight: 400,
                            }}
                        >
                            Search results are limited to the month currently in view. Navigate
                            to another month to see more results, or switch to{' '}
                            <strong>grid view</strong> for the best search experience.
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => setBannerDismissed(true)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'flex',
                        }}
                    >
                        <CloseIcon fill="#000" height='16px' width='16px' />
                    </button>
                </div>
            )}
            <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                views={['month']}
                date={visibleDate}
                popup
                formats={{ dateFormat: 'd' }}
                selectable={false}
                onNavigate={date => setVisibleDate(date)}
                components={{
                    toolbar: props => (
                        <CalendarToolbarWrapper
                            {...props}
                            visibleDate={visibleDate}
                            setVisibleDate={setVisibleDate}
                        />
                    ),
                    event: props => (
                        <CalendarEvent
                            {...props}
                            setActiveEvent={handleSetActiveEvent}
                            cancelDismiss={cancelDismiss}
                            scheduleDismiss={scheduleDismiss}
                        />
                    ),
                }}
                eventPropGetter={(event, start) => {
                    const viewMonth = localizer.startOf(visibleDate, 'month');
                    const eventMonth = localizer.startOf(start, 'month');
                    const isOffMonth =
                        viewMonth.getMonth() !== eventMonth.getMonth() ||
                        viewMonth.getFullYear() !== eventMonth.getFullYear();

                    if (isOffMonth) return { className: 'rbc-event-off-month' };
                    return {};
                }}
            />

            {activeEvent && (
                <EventCardHover
                    activeEvent={activeEvent}
                    cancelDismiss={cancelDismiss}
                    scheduleDismiss={scheduleDismiss}
                />
            )}
        </div>
    );
};

CalendarEvent.propTypes = {
    event: PropTypes.object.isRequired,
    setActiveEvent: PropTypes.func.isRequired,
    cancelDismiss: PropTypes.func.isRequired,
    scheduleDismiss: PropTypes.func.isRequired,
};

EventCalendar.propTypes = {
    hits: PropTypes.array.isRequired,
};

export default EventCalendar;